<template>
  <div v-if="showBanners">
    <banner
      v-for="usageBanner in activeBanners"
      :key="usageBanner.key"
      :color-scheme="usageBanner.colorScheme"
      :banner-message="usageBanner.message"
      :action-button-label="usageBanner.actionButtonLabel"
      has-action-button
      @click="routeToBilling(usageBanner.action)"
    />
  </div>
</template>

<script>
import Banner from 'dashboard/components/ui/Banner.vue';
import { mapGetters } from 'vuex';
import accountMixin from 'dashboard/mixins/account';

export default {
  components: { Banner },
  mixins: [accountMixin],
  computed: {
    ...mapGetters({
      isMacLimitReached: 'billing/isMacLimitReached',
      isLlmLimitReached: 'billing/isLlmLimitReached',
      macUsage: 'billing/macUsage',
      llmTokenUsage: 'billing/llmTokenUsage',
      macUsagePercent: 'billing/macUsagePercent',
      llmTokenUsagePercent: 'billing/llmTokenUsagePercent',
      subscriptionStatus: 'billing/subscriptionStatus',
    }),
    showBanners() {
      return this.activeBanners.length > 0;
    },
    activeBanners() {
      return [
        this.usageBanner({
          key: 'mac',
          usage: this.macUsage,
          percent: this.macUsagePercent,
          limitReached: this.isMacLimitReached,
          limitMessageKey: 'MAC_LIMIT',
          criticalMessageKey: 'MAC_CRITICAL',
          warningMessageKey: 'MAC_WARNING',
          action: 'purchase_mac',
          actionButtonKey: 'BUY_MORE_MAC',
        }),
        this.usageBanner({
          key: 'llm_tokens',
          usage: this.llmTokenUsage,
          percent: this.llmTokenUsagePercent,
          limitReached: this.isLlmLimitReached,
          limitMessageKey: 'LLM_LIMIT',
          criticalMessageKey: 'LLM_CRITICAL',
          warningMessageKey: 'LLM_WARNING',
          action: 'purchase_llm_tokens',
          actionButtonKey: 'BUY_MORE_LLM',
        }),
      ].filter(Boolean);
    },
  },
  mounted() {
    this.fetchBillingStatus();
  },
  methods: {
    fetchBillingStatus() {
      if (this.accountId) {
        this.$store.dispatch('billing/fetchBillingUsage');
      }
    },
    routeToBilling(action) {
      this.$router.push({
        name: 'billing_settings_index',
        params: { accountId: this.accountId },
        query: { action },
      });
    },
    usageBanner({
      key,
      usage,
      percent,
      limitReached,
      limitMessageKey,
      criticalMessageKey,
      warningMessageKey,
      action,
      actionButtonKey,
    }) {
      if (!usage?.effective_limit || percent < 80) return null;

      const severity = this.bannerSeverity({ percent, limitReached });
      const messageKey = this.messageKeyForSeverity({
        severity,
        limitMessageKey,
        criticalMessageKey,
        warningMessageKey,
      });

      return {
        key,
        colorScheme: severity.colorScheme,
        action,
        actionButtonLabel: this.$t(`BILLING.LIMIT_BANNER.${actionButtonKey}`),
        message: this.$t(`BILLING.LIMIT_BANNER.${messageKey}`, {
          used: this.formatNumber(usage.used),
          limit: this.formatNumber(usage.effective_limit),
          percent: Math.round(percent),
        }),
      };
    },
    bannerSeverity({ percent, limitReached }) {
      if (limitReached || percent >= 100) {
        return { level: 'limit', colorScheme: 'alert' };
      }
      if (percent >= 90) {
        return { level: 'critical', colorScheme: 'orange' };
      }
      return { level: 'warning', colorScheme: 'warning' };
    },
    messageKeyForSeverity({
      severity,
      limitMessageKey,
      criticalMessageKey,
      warningMessageKey,
    }) {
      if (severity.level === 'limit') return limitMessageKey;
      if (severity.level === 'critical') return criticalMessageKey;
      return warningMessageKey;
    },
    formatNumber(num) {
      if (!num && num !== 0) return '0';
      return num.toLocaleString();
    },
  },
};
</script>
